import React, { useState } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import useMeetingChatState from 'flow/hooks/useMeetingChatState';
import ContextMenu from 'flow/components/ContextMenu';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import Notification from 'flow/components/ui-components/Notification';
import MenuItemOpenChat from './MenuItemOpenChat';
import MenuItemShowJoinInfo from './MenuItemShowJoinInfo';
import MenuItemToggleHostControls from './MenuItemToggleHostControls';
import MenuItemToggleMembersList from './MenuItemToggleMembersList';
import BackgroundEffectsModal from 'flow/components/meeting/shared/BackgroundEffectsModal';
import MeetingDeviceSettingsDialog from 'flow/components/meeting/shared/MeetingDeviceSettingsDialog';

interface Props {
    openCloseJoinInfo: () => void;
}

const MoreMenu = ({ openCloseJoinInfo }: Props) => {
    const { i18next } = useAppContext();
    const { numberOfUnreadMessages } = useMeetingChatState();

    const [displayBackgroundEffects, setDisplayBackgroundEffects] =
        useState(false);
    const [displaySettings, setDisplaySettings] = useState(false);

    return (
        <>
            {/* Kept at this level (not inside a menu item) so they survive the
                menu closing on select — otherwise they would unmount instantly. */}
            {displayBackgroundEffects && (
                <BackgroundEffectsModal
                    onClose={() => setDisplayBackgroundEffects(false)}
                />
            )}
            {displaySettings && (
                <MeetingDeviceSettingsDialog
                    onClose={() => setDisplaySettings(false)}
                />
            )}
            <ContextMenu
                triggerLabel={i18next.t('menu.more_options')}
                icon={
                    <>
                        <Icon
                            iconKey="more_vert"
                            className="!h-6 !w-6 text-white"
                        />
                        {numberOfUnreadMessages > 0 && (
                            <Notification className="end-0 top-0">
                                {numberOfUnreadMessages}
                            </Notification>
                        )}
                    </>
                }
                /* `relative` anchors the unread badge to this button. Without
                   it the absolute badge escapes to the meeting container and
                   lands at the window edge. */
                className="hover:!brighten-10 relative flex h-12 w-12 items-center justify-center !rounded-xl !bg-gray-800 !p-0"
                menuClassName="no-dark min-w-[186px] !bg-gray-800 text-white"
                placement="top-end"
                preventOverflow={true}
            >
                <MenuItemToggleMembersList />
                <MenuItemToggleHostControls />
                <MenuItemOpenChat />
                <MenuItem
                    onClick={() => setDisplayBackgroundEffects(true)}
                    className="!bg-gray-800 !text-white hover:!bg-gray-700"
                >
                    <Icon iconKey="photo_filter" className="me-1.5" />
                    <div>
                        {i18next.t('videoconference.background_effects.title')}
                    </div>
                </MenuItem>
                <MenuItemShowJoinInfo onClick={openCloseJoinInfo} />
                <MenuItem
                    onClick={() => setDisplaySettings(true)}
                    className="!bg-gray-800 !text-white hover:!bg-gray-700"
                >
                    <Icon iconKey="general" className="me-1.5" />
                    <div>{i18next.t('settings')}</div>
                </MenuItem>
            </ContextMenu>
        </>
    );
};

export default MoreMenu;
